import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftRight,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  ShieldCheck,
  Unlock,
  X,
} from 'lucide-react';
import { Input, Textarea, cn } from '@clarix/ui';

/**
 * Developer debug tool for the global request/response payload encryption
 * (AES-256-GCM, Web Crypto). Paste an encrypted `{ iv, data, tag }` blob captured
 * from the Network tab / a log and reveal the plaintext, or encrypt a plaintext
 * payload — using a key you type in, NOT the build-time VITE_ENCRYPTION_KEY.
 *
 * Wire protocol MUST stay in lockstep with the backend @clarix/shared
 * `payload-crypto` and the frontend @clarix/api-client `payload-crypto`:
 *   - aes-256-gcm, 12-byte IV, 16-byte tag, HEX-encoded iv / data / tag.
 *   - Request body:  { iv, data, tag }.
 *   - Response body: { encrypted: true, iv, data, tag }.
 *   - Key: first 32 chars (utf8) of the encryption key.
 *
 * Everything runs client-side; nothing leaves the browser. Conversion is live.
 */
const ALGORITHM = 'AES-GCM';
const IV_LENGTH = 12;
const TAG_LENGTH = 16;

type Mode = 'encrypt' | 'decrypt';
type Kind = 'plain' | 'cipher';

interface EncryptedPayload {
  iv: string;
  data: string;
  tag: string;
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

function fromHex(hex: string): Uint8Array {
  const clean = hex.trim();
  if (clean.length % 2 !== 0 || /[^0-9a-fA-F]/.test(clean)) {
    throw new Error('Value is not valid hex.');
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

async function importAesKey(keyStr: string): Promise<CryptoKey> {
  // Backend uses the first 32 chars (utf8) of the key. AES-256 needs exactly 32 bytes.
  const keyData = new TextEncoder().encode(keyStr.slice(0, 32));
  if (keyData.length !== 32) {
    throw new Error(`Key must be at least 32 characters (got ${keyData.length} bytes).`);
  }
  return crypto.subtle.importKey('raw', keyData as BufferSource, { name: ALGORITHM }, false, [
    'encrypt',
    'decrypt',
  ]);
}

async function encryptPayload(plaintext: string, keyStr: string): Promise<EncryptedPayload> {
  const key = await importAesKey(keyStr);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: ALGORITHM, iv: iv as BufferSource, tagLength: 128 },
      key,
      new TextEncoder().encode(plaintext) as BufferSource,
    ),
  );
  // Web Crypto appends the 16-byte auth tag to the ciphertext; split it to match
  // the backend's separate { data, tag }.
  const data = ciphertext.slice(0, ciphertext.length - TAG_LENGTH);
  const tag = ciphertext.slice(ciphertext.length - TAG_LENGTH);
  return { iv: toHex(iv), data: toHex(data), tag: toHex(tag) };
}

async function decryptPayload(payload: EncryptedPayload, keyStr: string): Promise<string> {
  const key = await importAesKey(keyStr);
  const iv = fromHex(payload.iv);
  const data = fromHex(payload.data);
  const tag = fromHex(payload.tag);
  const combined = new Uint8Array(data.length + tag.length);
  combined.set(data);
  combined.set(tag, data.length);
  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv as BufferSource, tagLength: 128 },
    key,
    combined as BufferSource,
  );
  return new TextDecoder().decode(decrypted);
}

/** Pull { iv, data, tag } out of a pasted blob — accepts the raw envelope or a
 *  full response body ({ encrypted: true, iv, data, tag }). */
function parseEncryptedInput(raw: string): EncryptedPayload {
  const parsed = JSON.parse(raw) as Partial<EncryptedPayload> & { encrypted?: boolean };
  if (
    typeof parsed?.iv !== 'string' ||
    typeof parsed?.data !== 'string' ||
    typeof parsed?.tag !== 'string'
  ) {
    throw new Error('Expected JSON with string "iv", "data" and "tag" fields.');
  }
  return { iv: parsed.iv, data: parsed.data, tag: parsed.tag };
}

/** Pretty-print JSON if the plaintext is itself JSON, otherwise return as-is. */
function prettyMaybeJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function friendlyError(err: unknown, mode: Mode): string {
  const message = err instanceof Error ? err.message : String(err);
  if (mode === 'decrypt' && /operation-specific|decrypt/i.test(message)) {
    return 'Wrong key, or iv / data / tag do not match.';
  }
  return message;
}

/** Colour + icon for a panel by the KIND of content it holds (recolours on swap). */
function toneFor(kind: Kind) {
  return kind === 'cipher'
    ? {
        label: 'Encrypted payload',
        Icon: Lock,
        head: 'bg-warning-light text-warning-fg',
        ring: 'border-warning/40',
        dot: 'bg-warning',
      }
    : {
        label: 'Plaintext / JSON',
        Icon: Unlock,
        head: 'bg-success-light text-success-fg',
        ring: 'border-success/40',
        dot: 'bg-success',
      };
}

interface PanelProps {
  kind: Kind;
  value: string;
  onChange?: (v: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  header?: React.ReactNode;
}

function Panel({ kind, value, onChange, readOnly, placeholder, header }: PanelProps) {
  const tone = toneFor(kind);
  const { Icon } = tone;
  return (
    <div className={cn('bg-card flex min-h-0 flex-col overflow-hidden rounded-xl border shadow-sm', tone.ring)}>
      <div className={cn('flex items-center justify-between gap-2 border-b px-3.5 py-2', tone.head)}>
        <span className="flex items-center gap-1.5 text-lg font-semibold uppercase tracking-wide">
          <Icon className="h-3.5 w-3.5" /> {tone.label}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium tabular-nums opacity-70">
            {value.length.toLocaleString()} chars
          </span>
          {header}
        </div>
      </div>
      <Textarea
        readOnly={readOnly}
        spellCheck={false}
        placeholder={placeholder}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        className="min-h-0 flex-1 resize-none rounded-none border-0 bg-transparent p-3.5 font-mono text-lg leading-relaxed focus-visible:ring-0"
      />
    </div>
  );
}

export function DataAccessPage() {
  const [encryptionKey, setEncryptionKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [mode, setMode] = useState<Mode>('decrypt');
  const [source, setSource] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const keyBytes = useMemo(
    () => new TextEncoder().encode(encryptionKey.slice(0, 32)).length,
    [encryptionKey],
  );
  const keyReady = keyBytes === 32;

  // Live conversion — debounced so typing stays smooth. Every state write happens
  // inside the timer callback (never synchronously in the effect body) so we don't
  // trigger cascading renders — react-hooks/set-state-in-effect. Empty input skips
  // the debounce so the result clears immediately.
  useEffect(() => {
    let cancelled = false;
    const ready = Boolean(source.trim() && encryptionKey);
    const timer = window.setTimeout(async () => {
      if (!ready) {
        setError(null);
        setResult('');
        setBusy(false);
        return;
      }
      setError(null);
      setBusy(true);
      try {
        if (mode === 'encrypt') {
          const payload = await encryptPayload(source, encryptionKey);
          if (!cancelled) setResult(JSON.stringify({ encrypted: true, ...payload }, null, 2));
        } else {
          const payload = parseEncryptedInput(source);
          const text = await decryptPayload(payload, encryptionKey);
          if (!cancelled) setResult(prettyMaybeJson(text));
        }
      } catch (err) {
        if (!cancelled) {
          setResult('');
          setError(friendlyError(err, mode));
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, ready ? 300 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [source, encryptionKey, mode]);

  /** Flip the result back into the source and invert the mode — round-trips. */
  function handleSwap() {
    if (!result) return;
    setSource(result);
    setResult('');
    setMode((m) => (m === 'encrypt' ? 'decrypt' : 'encrypt'));
  }

  function copyResult() {
    if (!result) return;
    void navigator.clipboard.writeText(result).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  const sourceKind: Kind = mode === 'encrypt' ? 'plain' : 'cipher';
  const resultKind: Kind = mode === 'encrypt' ? 'cipher' : 'plain';

  return (
    <div className="bg-muted/40 text-foreground flex h-screen w-full flex-col overflow-hidden">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="bg-card flex flex-none flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-lg">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <div className="leading-tight">
            <h1 className="text-xl font-semibold tracking-tight">Payload Debugger</h1>
            <p className="text-muted-foreground text-sm">
              AES-256-GCM · runs locally, nothing leaves your browser
            </p>
          </div>
        </div>

        {/* Direction segmented control */}
        <div className="border-border bg-muted/60 inline-flex rounded-lg border p-1">
          {(['encrypt', 'decrypt'] as const).map((m) => {
            const active = mode === m;
            const Icon = m === 'encrypt' ? Lock : Unlock;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-lg font-semibold capitalize transition-all',
                  active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" /> {m}
              </button>
            );
          })}
        </div>
      </header>

      {/* ── Key row ─────────────────────────────────────────────── */}
      <div className="bg-card flex flex-none items-center gap-3 border-b px-5 py-2.5">
        <div className="relative flex-1">
          <KeyRound className="text-muted-foreground pointer-events-none absolute inset-y-0 left-3 my-auto h-3.5 w-3.5" />
          <Input
            type={showKey ? 'text' : 'password'}
            autoComplete="off"
            spellCheck={false}
            placeholder="Encryption key — first 32 characters are used…"
            value={encryptionKey}
            onChange={(e) => setEncryptionKey(e.target.value)}
            className="h-9 pl-9 pr-10 font-mono text-lg"
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            aria-label={showKey ? 'Hide key' : 'Show key'}
            className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center px-3"
          >
            {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <span
          className={cn(
            'flex flex-none items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-semibold',
            keyReady
              ? 'bg-success-light text-success-fg'
              : encryptionKey
                ? 'bg-warning-light text-warning-fg'
                : 'bg-muted text-muted-foreground',
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', keyReady ? 'bg-success' : 'bg-warning')} />
          {keyReady ? 'AES-256 ready' : `${keyBytes}/32 bytes`}
        </span>
      </div>

      {/* ── Editor (full-bleed, fills remaining height) ─────────── */}
      <main className="grid min-h-0 flex-1 grid-cols-1 items-stretch gap-3 p-4 md:grid-cols-[1fr_auto_1fr]">
        <Panel
          kind={sourceKind}
          value={source}
          onChange={setSource}
          placeholder={
            mode === 'encrypt'
              ? '{ "email": "user@example.com" }'
              : '{ "encrypted": true, "iv": "…", "data": "…", "tag": "…" }'
          }
          header={
            source ? (
              <button
                type="button"
                onClick={() => setSource('')}
                className="hover:bg-background/40 rounded p-0.5 transition-colors"
                title="Clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null
          }
        />

        {/* Middle: connector + swap */}
        <div className="flex flex-row items-center justify-center gap-2 md:flex-col md:gap-3">
          <span className="bg-border hidden h-full w-px md:block" />
          <button
            type="button"
            onClick={handleSwap}
            disabled={!result}
            title="Send result to input and flip direction"
            className={cn(
              'bg-card group flex h-10 w-10 flex-none items-center justify-center rounded-full border shadow-sm transition-all',
              result
                ? 'text-primary hover:border-primary/50 hover:bg-primary/5 cursor-pointer'
                : 'text-muted-foreground cursor-not-allowed opacity-50',
            )}
          >
            <ArrowLeftRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180" />
          </button>
          <span className="bg-border hidden h-full w-px md:block" />
        </div>

        <Panel
          kind={resultKind}
          value={result}
          readOnly
          placeholder="Result appears here as you type…"
          header={
            <button
              type="button"
              onClick={copyResult}
              disabled={!result}
              className={cn(
                'flex items-center gap-1 rounded px-1.5 py-0.5 text-sm font-semibold transition-colors',
                result ? 'hover:bg-background/40' : 'cursor-not-allowed opacity-50',
              )}
              title="Copy result"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          }
        />
      </main>

      {/* ── Status bar ──────────────────────────────────────────── */}
      <footer className="bg-card text-muted-foreground flex flex-none items-center justify-between gap-2 border-t px-5 py-2 text-lg">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'h-2 w-2 rounded-full transition-colors',
              error
                ? 'bg-destructive'
                : busy
                  ? 'bg-warning animate-pulse'
                  : result
                    ? 'bg-success'
                    : 'bg-muted-foreground/40',
            )}
          />
          {error ? (
            <span className="text-destructive font-medium">{error}</span>
          ) : busy ? (
            <span>Working…</span>
          ) : result ? (
            <span className="text-success-fg font-medium">
              {mode === 'encrypt' ? 'Encrypted' : 'Decrypted'} successfully
            </span>
          ) : !encryptionKey ? (
            <span>Enter a key to begin.</span>
          ) : (
            <span>Ready — type or paste on the left.</span>
          )}
        </span>
        <span className="tabular-nums">
          {source.length.toLocaleString()} → {result.length.toLocaleString()} chars
        </span>
      </footer>
    </div>
  );
}

export default DataAccessPage;
